// src/pages/menu-items/MenuItemList.jsx
import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  LinearProgress,
  Alert,
  Snackbar,
  TablePagination,
  InputAdornment,
  CircularProgress,
  Fab,
  Divider,
  Fade,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Card,
  CardContent,
  useTheme,
  alpha,
  ToggleButton,
  ToggleButtonGroup,
  Slider,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  RestaurantMenu as MenuIcon,
  CalendarToday as CalendarIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  AttachMoney as MoneyIcon,
  Category as CategoryIcon,
  LocalDining as FoodIcon,
  LocalDrink as DrinkIcon,
  FilterList as FilterIcon,
  Inventory as InventoryIcon,
  AddShoppingCart as StockIcon,
  History as HistoryIcon,
  ArrowUpward as InIcon,
  ArrowDownward as OutIcon,
  Store as StoreIcon,
} from '@mui/icons-material';

// Import theme colors and preloader
import { COLORS } from '../../../theme/colors';
import Preloader from '../../../theme/Preloader';

// Lazy load components
const AfricanSidebar = lazy(() => import('../../../components/RestaurantPOS/manager/african/Sidebar'));
const ContinentalSidebar = lazy(() => import('../../../components/RestaurantPOS/manager/continental/Sidebar'));
const LoungeSidebar = lazy(() => import('../../../components/RestaurantPOS/manager/lounge/Sidebar'));
const Header = lazy(() => import('../../../components/RestaurantPOS/manager/Header'));
const Footer = lazy(() => import('../../../components/RestaurantPOS/manager/Footer'));

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/pos_v1';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

// ============================================
// TOKEN UTILITY FUNCTIONS
// ============================================

const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    const payload = token.split('.')[1];
    if (!payload) return true;
    const decoded = JSON.parse(atob(payload));
    if (!decoded.exp) return true;
    return decoded.exp * 1000 < Date.now();
  } catch (err) {
    return true;
  }
};

const getTokenExpiryTime = (token) => {
  try {
    if (!token) return 0;
    const payload = token.split('.')[1];
    if (!payload) return 0;
    const decoded = JSON.parse(atob(payload));
    return decoded.exp ? decoded.exp * 1000 : 0;
  } catch (err) {
    return 0;
  }
};

// ============================================
// UTILITY FUNCTIONS & CONSTANTS
// ============================================

// Location configurations
const LOCATION_CONFIG = {
  african: {
    id: 5,
    name: 'African',
    color: COLORS.primary?.african || '#2E7D32',
    lightColor: '#4CAF50',
    icon: '🌍',
    sidebar: AfricanSidebar,
    path: '/african-menu-items'
  },
  continental: {
    id: 3,
    name: 'Continental',
    color: COLORS.primary?.continental || '#1565C0',
    lightColor: '#1976D2',
    icon: '🌐',
    sidebar: ContinentalSidebar,
    path: '/continental-menu-items'
  },
  lounge: {
    id: 4,
    name: 'Lounge',
    color: COLORS.primary?.lounge || '#6A1B9A',
    lightColor: '#9C27B0',
    icon: '🍸',
    sidebar: LoungeSidebar,
    path: '/lounge-menu-items'
  }
};

const MAIN_CATEGORIES = ['Food', 'Drinks'];

// ============================================
// SUB-COMPONENTS FOR BETTER PERFORMANCE
// ============================================

// Memoized Stats Card Component
const StatsCard = React.memo(({ stats, locationColor }) => {
  const theme = useTheme();
  
  const statItems = [
    { 
      label: 'Total Items', 
      value: stats.total, 
      color: locationColor,
      icon: <MenuIcon sx={{ fontSize: 20 }} />
    },
    { 
      label: 'Active', 
      value: stats.active, 
      color: '#4caf50',
      icon: <CheckCircleIcon sx={{ fontSize: 20 }} />
    },
    { 
      label: 'Inactive', 
      value: stats.inactive, 
      color: '#f44336',
      icon: <CancelIcon sx={{ fontSize: 20 }} />
    },
    { 
      label: 'Food Items', 
      value: stats.food || 0, 
      color: '#FF6F00',
      icon: <FoodIcon sx={{ fontSize: 20 }} />
    },
    { 
      label: 'Drinks', 
      value: stats.drinks || 0, 
      color: '#00695C',
      icon: <DrinkIcon sx={{ fontSize: 20 }} />
    },
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {statItems.map((item) => (
        <Grid item xs={12} sm={6} md={4} lg={2.4} key={item.label}>
          <Card
            sx={{
              borderRadius: 2,
              borderLeft: `4px solid ${item.color}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
              },
            }}
          >
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
                  {item.label}
                </Typography>
                {item.icon}
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: item.color }}>
                {item.value}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
});

// Memoized Menu Item Table Row Component with Inventory Actions
const MenuItemRow = React.memo(({ 
  item, 
  onEdit, 
  onDelete, 
  onToggle, 
  onView,
  onAddStock,
  onViewStock,
  index 
}) => {
  const theme = useTheme();
  const statusColor = item.is_active ? '#4caf50' : '#f44336';
  const statusLabel = item.is_active ? 'Active' : 'Inactive';
  const mainCategoryColor = item.main_category === 'Food' ? '#FF6F00' : '#00695C';

  return (
    <TableRow
      sx={{
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
        },
        transition: 'background-color 0.2s ease',
      }}
    >
      <TableCell sx={{ fontWeight: 600 }}>{index + 1}</TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MenuIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
          <Typography variant="body2" fontWeight={600}>
            {item.name}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {item.category}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={item.main_category}
          size="small"
          sx={{
            fontWeight: 700,
            backgroundColor: alpha(mainCategoryColor, 0.1),
            color: mainCategoryColor,
            fontSize: '0.75rem',
          }}
          icon={item.main_category === 'Food' ? <FoodIcon sx={{ fontSize: 14 }} /> : <DrinkIcon sx={{ fontSize: 14 }} />}
        />
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" fontWeight={700} color="primary">
          ₦{item.amount?.toFixed(2) || '0.00'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={400}
          sx={{
            fontSize: '0.85rem',
            maxWidth: '150px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.description || '-'}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={statusLabel}
          size="small"
          sx={{
            backgroundColor: alpha(statusColor, 0.1),
            color: statusColor,
            fontWeight: 700,
            fontSize: '0.75rem',
          }}
        />
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => onView('view', item)}
              sx={{ color: theme.palette.info.main }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => onEdit('edit', item)}
              sx={{ color: theme.palette.primary.main }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        
          <Tooltip title="View Stock">
            <IconButton
              size="small"
              onClick={() => onViewStock(item)}
              sx={{ color: theme.palette.info.main }}
            >
              <StoreIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={item.is_active ? 'Deactivate' : 'Activate'}>
            <IconButton
              size="small"
              onClick={() => onToggle(item)}
              sx={{ 
                color: item.is_active ? theme.palette.warning.main : theme.palette.success.main 
              }}
            >
              {item.is_active ? <CancelIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Permanently Delete">
            <IconButton
              size="small"
              onClick={() => onDelete(item)}
              sx={{ color: theme.palette.error.main }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
});

// ============================================
// MAIN COMPONENT
// ============================================

const MenuItemList = ({ locationKey = 'african' }) => {
  const theme = useTheme();
  
  // ============================================
  // STATE MANAGEMENT
  // ============================================

  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeMenu, setActiveMenu] = useState('Menu');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Stats state
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    food: 0,
    drinks: 0,
  });

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');

  // Filter state
  const [mainCategoryFilter, setMainCategoryFilter] = useState('all');
  const [priceRange, setPriceRange] = useState([0, 100000]);
  const [isPriceFilterActive, setIsPriceFilterActive] = useState(false);

  // Dialog states - Menu Item
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [selectedItem, setSelectedItem] = useState(null);

  // Form state - Menu Item
  const defaultFormData = useMemo(() => ({
    name: '',
    category: '',
    main_category: 'Food',
    description: '',
    amount: '',
    is_active: true,
    location_id: '',
    created_date: new Date().toISOString().split('T')[0],
  }), []);

  const [formData, setFormData] = useState(defaultFormData);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Toggle dialog state
  const [toggleDialogOpen, setToggleDialogOpen] = useState(false);
  const [itemToToggle, setItemToToggle] = useState(null);
  const [toggling, setToggling] = useState(false);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  // ============================================
  // INVENTORY STATE MANAGEMENT
  // ============================================

  // Stock dialog state
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [stockItem, setStockItem] = useState(null);
  const [stockQuantity, setStockQuantity] = useState('');
  const [stockCost, setStockCost] = useState('');
  const [stockSupplier, setStockSupplier] = useState('');
  const [stockNotes, setStockNotes] = useState('');
  const [stocking, setStocking] = useState(false);
  const [stockStep, setStockStep] = useState(0);

  // View Stock dialog state
  const [viewStockDialogOpen, setViewStockDialogOpen] = useState(false);
  const [viewStockItem, setViewStockItem] = useState(null);
  const [inventoryData, setInventoryData] = useState(null);
  const [stockMovements, setStockMovements] = useState([]);
  const [loadingStockData, setLoadingStockData] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Get location config
  const locationConfig = useMemo(() => 
    LOCATION_CONFIG[locationKey] || LOCATION_CONFIG.african,
    [locationKey]
  );
  
  const { name: locationName, id: locationIdValue, color: locationColor } = locationConfig;
  const SidebarComponent = locationConfig.sidebar;

  // ============================================
  // TOKEN MANAGEMENT FUNCTIONS
  // ============================================

  const getAuthToken = useCallback(() => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token || isTokenExpired(token)) {
        console.warn('[MenuItemList] Token invalid or expired');
        return null;
      }
      return token;
    } catch (err) {
      console.error('[MenuItemList] Error getting auth token:', err);
      return null;
    }
  }, []);

  const refreshAuthToken = useCallback(async () => {
    try {
      setIsRefreshing(true);
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(`${FULL_API_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Refresh failed');
      }

      const data = await response.json();
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        return data.access_token;
      }
      throw new Error('No access token in refresh response');
    } catch (err) {
      console.error('[MenuItemList] Token refresh failed:', err);
      localStorage.clear();
      window.location.href = '/restaurantAuth';
      return null;
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  // ============================================
  // API CALLS
  // ============================================

  const apiRequest = useCallback(async (endpoint, options = {}) => {
    let token = getAuthToken();
    
    if (!token) {
      console.log('[MenuItemList] Token invalid, attempting refresh...');
      token = await refreshAuthToken();
      if (!token) {
        throw new Error('Authentication required');
      }
    }

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = `${FULL_API_URL}${endpoint}`;

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (response.status === 401) {
        console.log('[MenuItemList] Received 401, attempting token refresh...');
        const newToken = await refreshAuthToken();
        if (newToken) {
          headers['Authorization'] = `Bearer ${newToken}`;
          const retryResponse = await fetch(url, {
            ...options,
            headers,
          });
          if (retryResponse.ok) {
            const contentType = retryResponse.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              return await retryResponse.json();
            }
            return { message: await retryResponse.text() };
          }
          if (retryResponse.status === 401) {
            throw new Error('Authentication failed after refresh');
          }
        }
        throw new Error('Authentication failed');
      }

      const contentType = response.headers.get('content-type');
      let data;
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = { message: text || 'Non-JSON response received' };
      }

      if (!response.ok) {
        const errorMessage = data.detail || data.message || `HTTP ${response.status}`;
        throw new Error(errorMessage);
      }

      return data;
    } catch (err) {
      console.error('[MenuItemList] API Error:', err);
      throw err;
    }
  }, [getAuthToken, refreshAuthToken]);

  // ============================================
  // FETCH FUNCTIONS
  // ============================================

  const fetchCategories = useCallback(async () => {
    setLoadingCategories(true);
    try {
      console.debug(`[MenuItemList] Fetching categories for ${locationName} location...`);
      const data = await apiRequest(`/categories/location/${locationName}`);
      console.debug('[MenuItemList] Categories fetched:', data);
      
      const categoriesData = Array.isArray(data) ? data : [];
      setCategories(categoriesData);
    } catch (err) {
      console.error('[MenuItemList] Error fetching categories:', err);
      if (err.message === 'Authentication required' || err.message === 'Authentication failed after refresh') {
        setSnackbar({
          open: true,
          message: 'Session expired. Please login again.',
          severity: 'error'
        });
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/restaurantAuth';
        }, 2000);
      } else {
        setSnackbar({
          open: true,
          message: `Failed to fetch categories: ${err.message}`,
          severity: 'error',
        });
      }
    } finally {
      setLoadingCategories(false);
    }
  }, [apiRequest, locationName]);

  const fetchMenuItems = useCallback(async () => {
    setLoading(true);
    try {
      console.debug(`[MenuItemList] Fetching menu items for ${locationName} location (ID: ${locationIdValue})...`);
      
      const endpoint = `/menu-items/?location_id=${locationIdValue}&limit=1000`;
      console.debug(`[MenuItemList] API Request: ${FULL_API_URL}${endpoint}`);
      
      const data = await apiRequest(endpoint);
      console.debug(`[MenuItemList] Menu items fetched:`, data);
      
      const itemsData = Array.isArray(data) ? data : [];
      console.debug(`[MenuItemList] Number of items received: ${itemsData.length}`);
      
      if (itemsData.length > 0) {
        console.debug(`[MenuItemList] Item names:`, itemsData.map(i => i.name));
        console.debug(`[MenuItemList] Item prices:`, itemsData.map(i => `${i.name}: ₦${i.amount}`));
      }
      
      setItems(itemsData);
      setFilteredItems(itemsData);
      
      const active = itemsData.filter(i => i.is_active).length;
      const food = itemsData.filter(i => i.main_category === 'Food').length;
      const drinks = itemsData.filter(i => i.main_category === 'Drinks').length;
      
      setStats({
        total: itemsData.length,
        active: active,
        inactive: itemsData.length - active,
        food: food,
        drinks: drinks,
      });
      
      if (itemsData.length > 0) {
        setSnackbar({
          open: true,
          message: `✅ Loaded ${itemsData.length} menu item(s) successfully!`,
          severity: 'success',
        });
      }
      
    } catch (err) {
      console.error(`[MenuItemList] Error fetching menu items:`, err);
      if (err.message === 'Authentication required' || err.message === 'Authentication failed after refresh') {
        setSnackbar({
          open: true,
          message: 'Session expired. Please login again.',
          severity: 'error'
        });
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/restaurantAuth';
        }, 2000);
      } else {
        setSnackbar({
          open: true,
          message: `❌ Failed to fetch menu items: ${err.message}`,
          severity: 'error',
        });
      }
    } finally {
      setLoading(false);
      setIsLoading(false);
    }
  }, [apiRequest, locationIdValue, locationName]);

  // ============================================
  // INVENTORY API FUNCTIONS
  // ============================================

  const fetchInventoryForMenuItem = useCallback(async (menuItemId) => {
    try {
      const endpoint = `/inventory/items?menu_item_id=${menuItemId}&location_id=${locationIdValue}`;
      const data = await apiRequest(endpoint);
      return Array.isArray(data) ? data[0] : null;
    } catch (err) {
      console.error('[MenuItemList] Error fetching inventory:', err);
      return null;
    }
  }, [apiRequest, locationIdValue]);

  const fetchStockMovements = useCallback(async (inventoryItemId) => {
    try {
      const endpoint = `/inventory/movements?inventory_item_id=${inventoryItemId}&limit=500`;
      const data = await apiRequest(endpoint);
      return Array.isArray(data) ? data : [];
    } catch (err) {
      console.error('[MenuItemList] Error fetching stock movements:', err);
      return [];
    }
  }, [apiRequest]);

  const createInventoryItem = useCallback(async (data) => {
    try {
      const submitData = {
        ...data,
        location_id: locationIdValue,
        quantity: data.quantity || 0,
        minimum_stock_level: data.minimum_stock_level || 5,
        reorder_point: data.reorder_point || 10,
        cost_per_unit: data.cost_per_unit || null,
        menu_item_id: data.menu_item_id,
        is_active: true,
      };
      
      const result = await apiRequest('/inventory/items', {
        method: 'POST',
        body: JSON.stringify(submitData),
      });
      
      return result;
    } catch (err) {
      console.error('[MenuItemList] Error creating inventory item:', err);
      throw err;
    }
  }, [apiRequest, locationIdValue]);

  const addStockToInventory = useCallback(async (inventoryItemId, quantity, reason, notes, costPerUnit) => {
    try {
      // First, get current inventory item
      const item = await apiRequest(`/inventory/items/${inventoryItemId}`);
      const currentQuantity = item.quantity || 0;
      const newQuantity = currentQuantity + quantity;
      
      // Update stock
      const result = await apiRequest(`/inventory/items/${inventoryItemId}/stock`, {
        method: 'PATCH',
        body: JSON.stringify({
          quantity: newQuantity,
          reason: reason || 'Stock addition from menu item',
          notes: notes || '',
        }),
      });
      
      // Create movement record with cost if provided
      if (costPerUnit) {
        await apiRequest('/inventory/movements', {
          method: 'POST',
          body: JSON.stringify({
            inventory_item_id: inventoryItemId,
            movement_type: 'PURCHASE',
            quantity: quantity,
            reason: reason || 'Stock purchase',
            cost_per_unit: costPerUnit,
            notes: notes || '',
          }),
        });
      }
      
      return result;
    } catch (err) {
      console.error('[MenuItemList] Error adding stock:', err);
      throw err;
    }
  }, [apiRequest]);

  // ============================================
  // CRUD OPERATIONS - Menu Items
  // ============================================

  const handleCreateMenuItem = useCallback(async (data) => {
    try {
      const submitData = {
        ...data,
        location_id: locationIdValue,
        amount: parseFloat(data.amount),
      };
      
      console.debug(`[MenuItemList] Creating menu item:`, submitData);
      const result = await apiRequest('/menu-items/', {
        method: 'POST',
        body: JSON.stringify(submitData),
      });
      
      setSnackbar({
        open: true,
        message: `✅ Menu item "${result.name}" created successfully!`,
        severity: 'success',
      });
      
      await fetchMenuItems();
      handleCloseDialog();
      return result;
    } catch (err) {
      console.error(`[MenuItemList] Error creating menu item:`, err);
      
      const errorMessage = err.message || '';
      let userFriendlyMessage = `❌ Failed to create menu item.`;
      
      if (errorMessage.toLowerCase().includes('already exists') || 
          errorMessage.toLowerCase().includes('duplicate entry')) {
        userFriendlyMessage = `❌ A menu item named "${data.name}" already exists. Please use a different name.`;
      } else if (errorMessage === 'Authentication required' || errorMessage === 'Authentication failed after refresh') {
        userFriendlyMessage = 'Session expired. Please login again.';
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/restaurantAuth';
        }, 2000);
      } else {
        userFriendlyMessage = `❌ Failed to create menu item: ${errorMessage}`;
      }
      
      setSnackbar({
        open: true,
        message: userFriendlyMessage,
        severity: 'error',
      });
      
      throw err;
    }
  }, [apiRequest, fetchMenuItems, locationIdValue]);

  const handleUpdateMenuItem = useCallback(async (id, data) => {
    try {
      const submitData = {
        ...data,
        location_id: locationIdValue,
        amount: data.amount ? parseFloat(data.amount) : undefined,
      };
      
      console.debug(`[MenuItemList] Updating menu item ${id}:`, submitData);
      const result = await apiRequest(`/menu-items/${id}`, {
        method: 'PUT',
        body: JSON.stringify(submitData),
      });
      
      setSnackbar({
        open: true,
        message: `✅ Menu item "${result.name}" updated successfully!`,
        severity: 'success',
      });
      
      await fetchMenuItems();
      handleCloseDialog();
      return result;
    } catch (err) {
      console.error(`[MenuItemList] Error updating menu item:`, err);
      
      const errorMessage = err.message || '';
      let userFriendlyMessage = `❌ Failed to update menu item.`;
      
      if (errorMessage.toLowerCase().includes('already exists') || 
          errorMessage.toLowerCase().includes('duplicate entry')) {
        userFriendlyMessage = `❌ A menu item named "${data.name}" already exists. Please use a different name.`;
      } else if (errorMessage === 'Authentication required' || errorMessage === 'Authentication failed after refresh') {
        userFriendlyMessage = 'Session expired. Please login again.';
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/restaurantAuth';
        }, 2000);
      } else {
        userFriendlyMessage = `❌ Failed to update menu item: ${errorMessage}`;
      }
      
      setSnackbar({
        open: true,
        message: userFriendlyMessage,
        severity: 'error',
      });
      
      throw err;
    }
  }, [apiRequest, fetchMenuItems, locationIdValue]);

  const handleToggleMenuItemStatus = useCallback(async (id, currentStatus) => {
    setToggling(true);
    try {
      const newStatus = !currentStatus;
      console.debug(`[MenuItemList] Toggling menu item ${id} status to ${newStatus}`);
      
      const result = await apiRequest(`/menu-items/${id}/toggle-status`, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: newStatus }),
      });
      
      setSnackbar({
        open: true,
        message: `✅ Menu item "${result.name}" ${newStatus ? 'activated' : 'deactivated'} successfully!`,
        severity: 'success',
      });
      
      await fetchMenuItems();
      handleCloseToggleDialog();
    } catch (err) {
      console.error(`[MenuItemList] Error toggling menu item status:`, err);
      
      const errorMessage = err.message || '';
      let userFriendlyMessage = `❌ Failed to toggle menu item status: ${errorMessage}`;
      
      if (errorMessage === 'Authentication required' || errorMessage === 'Authentication failed after refresh') {
        userFriendlyMessage = 'Session expired. Please login again.';
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/restaurantAuth';
        }, 2000);
      }
      
      setSnackbar({
        open: true,
        message: userFriendlyMessage,
        severity: 'error',
      });
    } finally {
      setToggling(false);
    }
  }, [apiRequest, fetchMenuItems]);

  const handlePermanentDeleteMenuItem = useCallback(async (id) => {
    setDeleting(true);
    try {
      console.debug(`[MenuItemList] Permanently deleting menu item ${id}`);
      
      const result = await apiRequest(`/menu-items/${id}/permanent`, {
        method: 'DELETE',
      });
      
      setSnackbar({
        open: true,
        message: `✅ ${result.message || 'Menu item permanently deleted successfully!'}`,
        severity: 'success',
      });
      
      await fetchMenuItems();
      handleCloseDeleteDialog();
    } catch (err) {
      console.error(`[MenuItemList] Error permanently deleting menu item:`, err);
      
      const errorMessage = err.message || '';
      let userFriendlyMessage = `❌ Failed to delete menu item: ${errorMessage}`;
      
      if (errorMessage === 'Authentication required' || errorMessage === 'Authentication failed after refresh') {
        userFriendlyMessage = 'Session expired. Please login again.';
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/restaurantAuth';
        }, 2000);
      }
      
      setSnackbar({
        open: true,
        message: userFriendlyMessage,
        severity: 'error',
      });
    } finally {
      setDeleting(false);
    }
  }, [apiRequest, fetchMenuItems]);

  // ============================================
  // INVENTORY CRUD OPERATIONS
  // ============================================

  const handleAddStock = useCallback(async () => {
    if (!stockItem) {
      setSnackbar({
        open: true,
        message: 'No item selected for stock addition',
        severity: 'error',
      });
      return;
    }
    
    const quantity = parseFloat(stockQuantity);
    if (!quantity || quantity <= 0) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid quantity greater than 0',
        severity: 'error',
      });
      return;
    }

    setStocking(true);
    try {
      // Check if inventory item exists for this menu item
      let inventoryItem = await fetchInventoryForMenuItem(stockItem.id);
      
      if (!inventoryItem) {
        // Create inventory item if it doesn't exist
        inventoryItem = await createInventoryItem({
          name: stockItem.name,
          description: stockItem.description || `Stock for ${stockItem.name}`,
          category: stockItem.category || 'Uncategorized',
          unit: 'unit',
          quantity: 0,
          minimum_stock_level: 5,
          reorder_point: 10,
          cost_per_unit: stockCost ? parseFloat(stockCost) : null,
          menu_item_id: stockItem.id,
          supplier: stockSupplier || '',
        });
        
        setSnackbar({
          open: true,
          message: `📦 Inventory item created for "${stockItem.name}"`,
          severity: 'info',
        });
      }
      
      // Add stock to inventory
      const result = await addStockToInventory(
        inventoryItem.id,
        quantity,
        `Stock added for menu item: ${stockItem.name}`,
        stockNotes || '',
        stockCost ? parseFloat(stockCost) : null
      );
      
      setSnackbar({
        open: true,
        message: `✅ Added ${quantity} units to "${stockItem.name}" stock successfully! Current stock: ${result.quantity}`,
        severity: 'success',
      });
      
      handleCloseStockDialog();
    } catch (err) {
      console.error('[MenuItemList] Error adding stock:', err);
      setSnackbar({
        open: true,
        message: `❌ Failed to add stock: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setStocking(false);
    }
  }, [
    stockItem, 
    stockQuantity, 
    stockCost, 
    stockSupplier, 
    stockNotes, 
    fetchInventoryForMenuItem,
    createInventoryItem,
    addStockToInventory
  ]);

  const handleViewStock = useCallback(async (item) => {
    setViewStockItem(item);
    setViewStockDialogOpen(true);
    setLoadingStockData(true);
    
    try {
      const inventory = await fetchInventoryForMenuItem(item.id);
      setInventoryData(inventory);
      
      if (inventory) {
        const movements = await fetchStockMovements(inventory.id);
        setStockMovements(movements);
      } else {
        setStockMovements([]);
      }
    } catch (err) {
      console.error('[MenuItemList] Error fetching stock data:', err);
      setSnackbar({
        open: true,
        message: `Failed to fetch stock data: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setLoadingStockData(false);
    }
  }, [fetchInventoryForMenuItem, fetchStockMovements]);

  // ============================================
  // DIALOG HANDLERS - Menu Items
  // ============================================

  const handleOpenDialog = useCallback((mode, item = null) => {
    console.debug(`[MenuItemList] Opening dialog in ${mode} mode:`, item);
    
    const today = new Date().toISOString().split('T')[0];
    
    setDialogMode(mode);
    setSelectedItem(item);
    setFormErrors({});
    setSubmitting(false);
    
    let newFormData;
    if (mode === 'create') {
      newFormData = {
        name: '',
        category: '',
        main_category: 'Food',
        description: '',
        amount: '',
        is_active: true,
        location_id: locationIdValue,
        created_date: today,
      };
    } else if (item) {
      newFormData = {
        name: item.name || '',
        category: item.category || '',
        main_category: item.main_category || 'Food',
        description: item.description || '',
        amount: item.amount?.toString() || '',
        is_active: item.is_active !== undefined ? item.is_active : true,
        location_id: locationIdValue,
        created_date: item.created_date || today,
      };
    } else {
      newFormData = defaultFormData;
    }
    
    setFormData(newFormData);
    setDialogOpen(true);
  }, [locationIdValue, defaultFormData]);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setSelectedItem(null);
    setFormErrors({});
    setSubmitting(false);
    setFormData(defaultFormData);
  }, [defaultFormData]);

  const handleOpenToggleDialog = useCallback((item) => {
    console.debug('[MenuItemList] Opening toggle dialog for:', item);
    setItemToToggle(item);
    setToggleDialogOpen(true);
  }, []);

  const handleCloseToggleDialog = useCallback(() => {
    setToggleDialogOpen(false);
    setItemToToggle(null);
    setToggling(false);
  }, []);

  const handleOpenDeleteDialog = useCallback((item) => {
    console.debug('[MenuItemList] Opening delete dialog for:', item);
    setItemToDelete(item);
    setDeleteConfirmation('');
    setDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
    setDeleteConfirmation('');
    setDeleting(false);
  }, []);

  // ============================================
  // DIALOG HANDLERS - Inventory
  // ============================================

  const handleOpenStockDialog = useCallback((item) => {
    setStockItem(item);
    setStockQuantity('');
    setStockCost('');
    setStockSupplier('');
    setStockNotes('');
    setStockStep(0);
    setStockDialogOpen(true);
  }, []);

  const handleCloseStockDialog = useCallback(() => {
    setStockDialogOpen(false);
    setStockItem(null);
    setStockQuantity('');
    setStockCost('');
    setStockSupplier('');
    setStockNotes('');
    setStocking(false);
    setStockStep(0);
  }, []);

  const handleCloseViewStockDialog = useCallback(() => {
    setViewStockDialogOpen(false);
    setViewStockItem(null);
    setInventoryData(null);
    setStockMovements([]);
    setLoadingStockData(false);
  }, []);

  // ============================================
  // FORM HANDLERS - Menu Items
  // ============================================

  const handleFormChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [formErrors]);

  const validateForm = useCallback(() => {
    const errors = {};
    const trimmedName = formData?.name?.trim() || '';
    const trimmedCategory = formData?.category?.trim() || '';
    const amount = parseFloat(formData?.amount);
    
    if (!trimmedName) {
      errors.name = 'Item name is required';
    } else if (trimmedName.length < 2) {
      errors.name = 'Item name must be at least 2 characters';
    } else if (trimmedName.length > 100) {
      errors.name = 'Item name must be 100 characters or less';
    } else if (!/^[a-zA-Z0-9\s\-_&'\.]+$/.test(trimmedName)) {
      errors.name = 'Item name contains invalid characters';
    }
    
    if (!trimmedCategory) {
      errors.category = 'Category is required';
    } else if (trimmedCategory.length < 2) {
      errors.category = 'Category must be at least 2 characters';
    } else if (trimmedCategory.length > 50) {
      errors.category = 'Category must be 50 characters or less';
    }
    
    if (!formData?.main_category) {
      errors.main_category = 'Main category is required';
    }
    
    if (!formData?.amount) {
      errors.amount = 'Amount is required';
    } else if (isNaN(amount) || amount <= 0) {
      errors.amount = 'Amount must be a positive number';
    } else if (amount > 999999.99) {
      errors.amount = 'Amount cannot exceed 999,999.99';
    }
    
    const existing = items.find(i => 
      i.name?.toLowerCase() === trimmedName.toLowerCase() && 
      i.id !== selectedItem?.id
    );
    if (existing) {
      errors.name = `"${trimmedName}" already exists. Please use a different name.`;
    }
    
    if (!formData?.created_date) {
      errors.created_date = 'Created date is required';
    }
    
    setFormErrors(errors);
    console.debug('[MenuItemList] Validation result:', Object.keys(errors).length === 0 ? '✅ Valid' : errors);
    return Object.keys(errors).length === 0;
  }, [formData, items, selectedItem]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const submitData = {
      ...formData,
      name: formData.name.trim(),
      category: formData.category.trim(),
      amount: parseFloat(formData.amount),
      location_id: locationIdValue,
    };
    
    setSubmitting(true);
    
    try {
      if (dialogMode === 'create') {
        await handleCreateMenuItem(submitData);
      } else if (dialogMode === 'edit' && selectedItem) {
        await handleUpdateMenuItem(selectedItem.id, submitData);
      }
    } catch (err) {
      console.error('[MenuItemList] Submit error:', err);
    } finally {
      setSubmitting(false);
    }
  }, [formData, dialogMode, selectedItem, validateForm, handleCreateMenuItem, handleUpdateMenuItem, locationIdValue]);

  // ============================================
  // SEARCH AND FILTER
  // ============================================

  const applyFilters = useCallback(() => {
    let filtered = [...items];
    
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(item =>
        item.name?.toLowerCase().includes(term) ||
        item.category?.toLowerCase().includes(term) ||
        item.description?.toLowerCase().includes(term)
      );
    }
    
    if (mainCategoryFilter !== 'all') {
      filtered = filtered.filter(item => item.main_category === mainCategoryFilter);
    }
    
    if (isPriceFilterActive) {
      filtered = filtered.filter(item => 
        item.amount >= priceRange[0] && item.amount <= priceRange[1]
      );
    }
    
    setFilteredItems(filtered);
    setPage(0);
  }, [items, searchTerm, mainCategoryFilter, priceRange, isPriceFilterActive]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleSearch = useCallback((event) => {
    const term = event.target.value;
    setSearchTerm(term);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  const handleMainCategoryFilterChange = useCallback((event, newValue) => {
    if (newValue !== null) {
      setMainCategoryFilter(newValue);
    }
  }, []);

  const handlePriceRangeChange = useCallback((event, newValue) => {
    setPriceRange(newValue);
    setIsPriceFilterActive(true);
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setSearchTerm('');
    setMainCategoryFilter('all');
    setPriceRange([0, 100000]);
    setIsPriceFilterActive(false);
    setPage(0);
  }, []);

  // ============================================
  // SORTING
  // ============================================

  const handleSort = useCallback((property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    
    const sorted = [...filteredItems].sort((a, b) => {
      if (property === 'name') {
        return isAsc ? a.name?.localeCompare(b.name) : b.name?.localeCompare(a.name);
      }
      if (property === 'amount') {
        return isAsc ? (a.amount || 0) - (b.amount || 0) : (b.amount || 0) - (a.amount || 0);
      }
      if (property === 'category') {
        return isAsc ? a.category?.localeCompare(b.category) : b.category?.localeCompare(a.category);
      }
      if (property === 'created_date') {
        return isAsc ? new Date(a.created_date) - new Date(b.created_date) : new Date(b.created_date) - new Date(a.created_date);
      }
      return 0;
    });
    setFilteredItems(sorted);
  }, [orderBy, order, filteredItems]);

  // ============================================
  // SESSION MANAGEMENT
  // ============================================

  useEffect(() => {
    const checkAndRefreshToken = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const expiryTime = getTokenExpiryTime(token);
      const timeLeft = expiryTime - Date.now();

      if (timeLeft > 0 && timeLeft < 120000) {
        console.log('[MenuItemList] Auto-refreshing token...');
        await refreshAuthToken();
      }
    };

    const refreshInterval = setInterval(checkAndRefreshToken, 60000);
    return () => clearInterval(refreshInterval);
  }, [refreshAuthToken]);

  // ============================================
  // EFFECTS
  // ============================================

  const handleRefresh = useCallback(() => {
    console.debug('[MenuItemList] Manual refresh triggered');
    setRefreshKey(prev => prev + 1);
    fetchMenuItems();
    fetchCategories();
  }, [fetchMenuItems, fetchCategories]);

  useEffect(() => {
    console.debug(`[MenuItemList] Component mounted for ${locationName}...`);
    fetchMenuItems();
    fetchCategories();
  }, [fetchMenuItems, locationName, fetchCategories, refreshKey]);

  // ============================================
  // RENDER
  // ============================================

  if (isLoading || isRefreshing) {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <Preloader text={`Loading ${locationName} Menu Items...`} size={70} fullScreen={true} />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="epos-wrapper">
        <div className="epos-main-layout">
          <SidebarComponent activeMenu={activeMenu} setActiveMenu={setActiveMenu} />

          <div className="epos-main">
            <Header />
            
            <div className="epos-content">
              {/* Hero Card */}
              <div className="hero-card">
                <div>
                  <div className="hero-title" style={{ color: locationColor, fontWeight: 800 }}>
                    {locationConfig.icon} {locationName} Menu Items
                  </div>
                  <div className="hero-text-wrapper">
                    <div className="hero-tagline">
                      <span className="hero-tagline-icon">✦</span>
                      Manage food and beverage items with inventory tracking
                    </div>
                  </div>
                </div>
                <button 
                  className="new-order-btn" 
                  onClick={() => handleOpenDialog('create')}
                  style={{ backgroundColor: locationColor }}
                >
                  <AddIcon style={{ marginRight: '8px' }} />
                  Add Menu Item
                </button>
              </div>

              {/* Stats Cards */}
              <StatsCard stats={stats} locationColor={locationColor} />

              {/* Search and Filters Bar */}
              <Paper
                sx={{
                  p: 2,
                  mb: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: '200px' }}>
                    <TextField
                      size="small"
                      placeholder="Search items by name, category, or description..."
                      value={searchTerm}
                      onChange={handleSearch}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                        endAdornment: searchTerm && (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={handleClearSearch}>
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ flex: 1, maxWidth: '400px' }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {filteredItems.length} items found
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Clear All Filters">
                      <IconButton onClick={handleClearAllFilters} color="primary" size="small">
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Refresh">
                      <IconButton onClick={handleRefresh} disabled={loading} size="small">
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FilterIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Filter by:
                    </Typography>
                  </Box>
                  
                  <ToggleButtonGroup
                    value={mainCategoryFilter}
                    exclusive
                    onChange={handleMainCategoryFilterChange}
                    size="small"
                    aria-label="main category filter"
                  >
                    <ToggleButton value="all" aria-label="all">
                      All
                    </ToggleButton>
                    <ToggleButton value="Food" aria-label="food">
                      <FoodIcon sx={{ fontSize: 16, mr: 0.5 }} /> Food
                    </ToggleButton>
                    <ToggleButton value="Drinks" aria-label="drinks">
                      <DrinkIcon sx={{ fontSize: 16, mr: 0.5 }} /> Drinks
                    </ToggleButton>
                  </ToggleButtonGroup>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: '200px' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Price: ₦{priceRange[0]} - ₦{priceRange[1]}
                      {isPriceFilterActive && (
                        <Chip 
                          label="Filter Active" 
                          size="small" 
                          color="primary" 
                          sx={{ ml: 1, height: 20, fontSize: '0.6rem' }}
                        />
                      )}
                    </Typography>
                    <Slider
                      value={priceRange}
                      onChange={handlePriceRangeChange}
                      valueLabelDisplay="auto"
                      min={0}
                      max={100000}
                      size="small"
                      sx={{ width: 200 }}
                    />
                  </Box>
                </Box>
              </Paper>

              {/* Loading Indicator */}
              {loading && (
                <Box sx={{ width: '100%', mb: 2 }}>
                  <LinearProgress color="primary" />
                </Box>
              )}

              {/* Menu Items Table */}
              <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
                        <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <TableSortLabel
                            active={orderBy === 'name'}
                            direction={orderBy === 'name' ? order : 'asc'}
                            onClick={() => handleSort('name')}
                          >
                            Item Name
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <TableSortLabel
                            active={orderBy === 'category'}
                            direction={orderBy === 'category' ? order : 'asc'}
                            onClick={() => handleSort('category')}
                          >
                            Category
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Main Category</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          <TableSortLabel
                            active={orderBy === 'amount'}
                            direction={orderBy === 'amount' ? order : 'asc'}
                            onClick={() => handleSort('amount')}
                          >
                            Price (₦)
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredItems.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                            <MenuIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                            <Typography variant="h6" color="textSecondary" gutterBottom sx={{ fontWeight: 600 }}>
                              No Menu Items Found
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 400 }}>
                              {items.length > 0 ? 'Try adjusting your search or filters' : 'Start by adding your first menu item'}
                            </Typography>
                            {items.length > 0 && (
                              <Button 
                                variant="outlined" 
                                size="small" 
                                onClick={handleClearAllFilters}
                                sx={{ mt: 2 }}
                              >
                                Clear All Filters
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredItems
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((item, index) => (
                            <MenuItemRow
                              key={item.id}
                              item={item}
                              index={page * rowsPerPage + index}
                              onView={handleOpenDialog}
                              onEdit={handleOpenDialog}
                              onToggle={handleOpenToggleDialog}
                              onDelete={handleOpenDeleteDialog}
                              onAddStock={handleOpenStockDialog}
                              onViewStock={handleViewStock}
                            />
                          ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {/* Pagination */}
                <TablePagination
                  component="div"
                  count={filteredItems.length}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  sx={{
                    '& .MuiTablePagination-displayedRows': {
                      fontWeight: 500,
                    },
                    '& .MuiTablePagination-selectLabel': {
                      fontWeight: 500,
                    },
                  }}
                />
              </Paper>
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {/* ============================================ */}
      {/* CREATE/EDIT/VIEW DIALOG - Menu Item */}
      {/* ============================================ */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        TransitionComponent={Fade}
        transitionDuration={200}
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh',
          },
        }}
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {dialogMode === 'create' && <AddIcon color="primary" />}
              {dialogMode === 'edit' && <EditIcon color="info" />}
              {dialogMode === 'view' && <ViewIcon color="primary" />}
              <Typography variant="h6" fontWeight={700}>
                {dialogMode === 'create' && `Create Menu Item - ${locationName}`}
                {dialogMode === 'edit' && `Edit Menu Item - ${locationName}`}
                {dialogMode === 'view' && `Menu Item Details - ${locationName}`}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          
          <DialogContent dividers sx={{ pt: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Item Name"
                  value={formData?.name || ''}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  disabled={dialogMode === 'view'}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MenuIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  autoFocus={dialogMode !== 'view'}
                  placeholder="e.g., Grilled Chicken, Fresh Salad, Cappuccino"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl 
                  fullWidth 
                  error={!!formErrors.category} 
                  disabled={dialogMode === 'view'}
                >
                  <InputLabel required sx={{ fontWeight: 500 }}>Category</InputLabel>
                  <Select
                    value={formData?.category || ''}
                    onChange={(e) => handleFormChange('category', e.target.value)}
                    label="Category"
                  >
                    <MenuItem value="" disabled>Select a category</MenuItem>
                    {loadingCategories ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} /> Loading...
                      </MenuItem>
                    ) : categories.length > 0 ? (
                      categories.map((cat) => (
                        <MenuItem key={cat.id} value={cat.name}>
                          {cat.name}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No categories available</MenuItem>
                    )}
                  </Select>
                  {formErrors.category && (
                    <FormHelperText>{formErrors.category}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl 
                  fullWidth 
                  error={!!formErrors.main_category} 
                  disabled={dialogMode === 'view'}
                >
                  <InputLabel required sx={{ fontWeight: 500 }}>Main Category</InputLabel>
                  <Select
                    value={formData?.main_category || 'Food'}
                    onChange={(e) => handleFormChange('main_category', e.target.value)}
                    label="Main Category"
                  >
                    {MAIN_CATEGORIES.map(cat => (
                      <MenuItem key={cat} value={cat}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {cat === 'Food' ? <FoodIcon fontSize="small" /> : <DrinkIcon fontSize="small" />}
                          {cat}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.main_category && (
                    <FormHelperText>{formErrors.main_category}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Price (₦)"
                  type="number"
                  value={formData?.amount || ''}
                  onChange={(e) => handleFormChange('amount', e.target.value)}
                  error={!!formErrors.amount}
                  helperText={formErrors.amount}
                  disabled={dialogMode === 'view'}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MoneyIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    inputProps: {
                      step: '0.01',
                      min: '0.01',
                    },
                  }}
                  placeholder="0.00"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={dialogMode === 'view'}>
                  <InputLabel sx={{ fontWeight: 500 }}>Status</InputLabel>
                  <Select
                    value={formData?.is_active ? 'active' : 'inactive'}
                    onChange={(e) => handleFormChange('is_active', e.target.value === 'active')}
                    label="Status"
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData?.description || ''}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  disabled={dialogMode === 'view'}
                  multiline
                  rows={3}
                  placeholder="Brief description of the item"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignItems: 'flex-start', pt: 1.5 }}>
                        <CategoryIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Created Date"
                  type="date"
                  value={formData?.created_date || ''}
                  onChange={(e) => handleFormChange('created_date', e.target.value)}
                  error={!!formErrors.created_date}
                  helperText={formErrors.created_date}
                  disabled={dialogMode === 'view'}
                  required
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button onClick={handleCloseDialog} color="inherit" sx={{ fontWeight: 500 }}>
              {dialogMode === 'view' ? 'Close' : 'Cancel'}
            </Button>
            {dialogMode !== 'view' && (
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={dialogMode === 'create' ? <SaveIcon /> : <SaveIcon />}
                disabled={submitting}
                sx={{
                  backgroundColor: locationColor,
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: locationColor,
                    opacity: 0.9,
                  },
                }}
              >
                {submitting ? <CircularProgress size={24} color="inherit" /> : (dialogMode === 'create' ? 'Create Item' : 'Update Item')}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>

      {/* ============================================ */}
      {/* TOGGLE DIALOG */}
      {/* ============================================ */}
      <Dialog
        open={toggleDialogOpen}
        onClose={handleCloseToggleDialog}
        TransitionComponent={Fade}
        transitionDuration={200}
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
            maxWidth: '500px',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {itemToToggle?.is_active ? (
            <CancelIcon color="warning" />
          ) : (
            <CheckCircleIcon color="success" />
          )}
          <Typography variant="h6" fontWeight={700}>
            {itemToToggle?.is_active ? 'Deactivate' : 'Activate'} Menu Item
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            <InfoIcon color="info" sx={{ mt: 0.5 }} />
            <Typography variant="body1" fontWeight={400}>
              Are you sure you want to <strong>{itemToToggle?.is_active ? 'deactivate' : 'activate'}</strong> menu item <strong>"{itemToToggle?.name}"</strong>?
            </Typography>
          </Box>
          
          <Alert severity={itemToToggle?.is_active ? 'warning' : 'info'} sx={{ mt: 2 }}>
            <Typography fontWeight={400}>
              {itemToToggle?.is_active 
                ? 'Deactivating will hide this item from the menu. Customers will not be able to order it.'
                : 'Activating will make this item visible in the menu again. Customers will be able to order it.'}
            </Typography>
          </Alert>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseToggleDialog} color="inherit" sx={{ fontWeight: 500 }}>
            Cancel
          </Button>
          <Button
            onClick={() => handleToggleMenuItemStatus(itemToToggle?.id, itemToToggle?.is_active)}
            variant="contained"
            color={itemToToggle?.is_active ? 'warning' : 'success'}
            startIcon={itemToToggle?.is_active ? <CancelIcon /> : <CheckCircleIcon />}
            disabled={toggling}
            sx={{ fontWeight: 600 }}
          >
            {toggling ? <CircularProgress size={24} /> : (itemToToggle?.is_active ? 'Deactivate' : 'Activate')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================ */}
      {/* DELETE DIALOG */}
      {/* ============================================ */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        TransitionComponent={Fade}
        transitionDuration={200}
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
            maxWidth: '550px',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          <Typography variant="h6" fontWeight={700} color="error">
            Permanent Delete
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
            <WarningIcon color="error" sx={{ mt: 0.5, fontSize: 32 }} />
            <Box>
              <Typography variant="body1" fontWeight={400} gutterBottom>
                You are about to <strong style={{ color: '#d32f2f' }}>permanently delete</strong> menu item <strong>"{itemToDelete?.name}"</strong>.
              </Typography>
              <Typography variant="body2" color="textSecondary" fontWeight={400}>
                This action <strong style={{ color: '#d32f2f' }}>cannot be undone</strong> and will permanently remove this item from the system.
              </Typography>
            </Box>
          </Box>
          
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={400}>
              <strong>Warning:</strong> Deleting this item will remove it from all orders and menus associated with it.
            </Typography>
          </Alert>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight={500} gutterBottom>
              To confirm deletion, type the item name <strong>"{itemToDelete?.name}"</strong> below:
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={`Type "${itemToDelete?.name}" to confirm`}
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              error={deleteConfirmation !== '' && deleteConfirmation !== itemToDelete?.name}
              helperText={deleteConfirmation !== '' && deleteConfirmation !== itemToDelete?.name ? 'Item name does not match' : ''}
              sx={{ mt: 1 }}
              autoFocus
            />
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseDeleteDialog} color="inherit" sx={{ fontWeight: 500 }}>
            Cancel
          </Button>
          <Button
            onClick={() => handlePermanentDeleteMenuItem(itemToDelete?.id)}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={deleting || deleteConfirmation !== itemToDelete?.name}
            sx={{
              fontWeight: 600,
              '&:disabled': {
                opacity: 0.5,
              },
            }}
          >
            {deleting ? <CircularProgress size={24} color="inherit" /> : 'Permanently Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================ */}
      {/* ADD STOCK DIALOG */}
      {/* ============================================ */}
      <Dialog
        open={stockDialogOpen}
        onClose={handleCloseStockDialog}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              <StockIcon sx={{ mr: 1, color: '#4caf50' }} />
              Add Stock to Menu Item
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {stockItem?.name} - ₦{stockItem?.amount?.toFixed(2) || '0.00'}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseStockDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers sx={{ pt: 3 }}>
          <Stepper activeStep={stockStep} sx={{ mb: 3 }}>
            <Step>
              <StepLabel>Quantity</StepLabel>
            </Step>
            <Step>
              <StepLabel>Details</StepLabel>
            </Step>
            <Step>
              <StepLabel>Confirm</StepLabel>
            </Step>
          </Stepper>

          <Alert severity="info" sx={{ mb: 3 }}>
            Adding stock will create or update an inventory item linked to this menu item.
         
          </Alert>

          {stockStep === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Quantity to Add"
                  type="number"
                  value={stockQuantity}
                  onChange={(e) => setStockQuantity(e.target.value)}
                  required
                  autoFocus
                  InputProps={{
                    inputProps: { min: 0.01, step: 0.01 },
                    endAdornment: (
                      <InputAdornment position="end">unit</InputAdornment>
                    ),
                  }}
                />
              </Grid>
             
            </Grid>
          )}

          {stockStep === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Supplier(Optional)"
                  value={stockSupplier}
                  onChange={(e) => setStockSupplier(e.target.value)}
                  placeholder="Optional"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes(Optional)"
                  multiline
                  rows={3}
                  value={stockNotes}
                  onChange={(e) => setStockNotes(e.target.value)}
                  placeholder="Additional notes about this stock addition"
                />
              </Grid>
            </Grid>
          )}

          {stockStep === 2 && (
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Confirm Stock Addition
              </Typography>
              <Box sx={{ bgcolor: alpha('#4caf50', 0.04), p: 2, borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Item</Typography>
                    <Typography variant="body2" fontWeight={500}>{stockItem?.name}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Quantity</Typography>
                    <Typography variant="body2" fontWeight={700} color="#4caf50">
                      +{stockQuantity} units
                    </Typography>
                  </Grid>
                  {stockCost && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Cost Per Unit</Typography>
                      <Typography variant="body2">₦{parseFloat(stockCost).toFixed(2)}</Typography>
                    </Grid>
                  )}
                  {stockSupplier && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Supplier</Typography>
                      <Typography variant="body2">{stockSupplier}</Typography>
                    </Grid>
                  )}
                  {stockNotes && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Notes</Typography>
                      <Typography variant="body2">{stockNotes}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseStockDialog} color="inherit">
            Cancel
          </Button>
          {stockStep > 0 && (
            <Button onClick={() => setStockStep(prev => prev - 1)}>
              Back
            </Button>
          )}
          {stockStep < 2 ? (
            <Button
              variant="contained"
              onClick={() => setStockStep(prev => prev + 1)}
              disabled={(stockStep === 0 && !stockQuantity) || (stockStep === 1 && false)}
              sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#388e3c' } }}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleAddStock}
              disabled={stocking || !stockQuantity}
              sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#388e3c' } }}
            >
              {stocking ? <CircularProgress size={24} /> : 'Confirm Add Stock'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ============================================ */}
      {/* VIEW STOCK DIALOG */}
      {/* ============================================ */}
      <Dialog
        open={viewStockDialogOpen}
        onClose={handleCloseViewStockDialog}
        maxWidth="lg"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '85vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              <StoreIcon sx={{ mr: 1, color: theme.palette.info.main }} />
              Stock Information: {viewStockItem?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {viewStockItem?.category} • ₦{viewStockItem?.amount?.toFixed(2) || '0.00'}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseViewStockDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers>
          {loadingStockData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : inventoryData ? (
            <>
              {/* Stock Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: alpha('#4caf50', 0.04), borderLeft: '4px solid #4caf50' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Current Stock</Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {inventoryData.quantity} {inventoryData.unit}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: alpha('#ff9800', 0.04), borderLeft: '4px solid #ff9800' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Reorder Point</Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {inventoryData.reorder_point} {inventoryData.unit}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: alpha('#2196f3', 0.04), borderLeft: '4px solid #2196f3' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Status</Typography>
                      <Chip
                        label={inventoryData.status?.replace('_', ' ') || 'Unknown'}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          backgroundColor: alpha(
                            inventoryData.status === 'IN_STOCK' ? '#4caf50' :
                            inventoryData.status === 'LOW_STOCK' ? '#ff9800' :
                            inventoryData.status === 'OUT_OF_STOCK' ? '#f44336' : '#9e9e9e',
                            0.1
                          ),
                          color: inventoryData.status === 'IN_STOCK' ? '#4caf50' :
                                 inventoryData.status === 'LOW_STOCK' ? '#ff9800' :
                                 inventoryData.status === 'OUT_OF_STOCK' ? '#f44336' : '#9e9e9e',
                        }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: alpha('#9c27b0', 0.04), borderLeft: '4px solid #9c27b0' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Total Value</Typography>
                      <Typography variant="h5" fontWeight={700}>
                        ₦{((inventoryData.quantity || 0) * (inventoryData.cost_per_unit || 0)).toFixed(2)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Stock Movements History */}
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                <HistoryIcon sx={{ mr: 1, fontSize: 20, verticalAlign: 'middle' }} />
                Stock Movement History
              </Typography>
              
              {stockMovements.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="textSecondary">
                    No stock movements recorded for this item
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Quantity</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Previous</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>New</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stockMovements.slice(0, 50).map((movement) => {
                        const isIn = movement.movement_type === 'PURCHASE' || movement.movement_type === 'RETURN';
                        return (
                          <TableRow key={movement.id}>
                            <TableCell>
                              {new Date(movement.created_at).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={movement.movement_type}
                                size="small"
                                sx={{
                                  backgroundColor: alpha(
                                    isIn ? '#4caf50' : '#f44336',
                                    0.1
                                  ),
                                  color: isIn ? '#4caf50' : '#f44336',
                                  fontWeight: 600,
                                }}
                                icon={isIn ? <InIcon fontSize="small" /> : <OutIcon fontSize="small" />}
                              />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: isIn ? '#4caf50' : '#f44336' }}>
                              {isIn ? '+' : '-'}{movement.quantity} {movement.unit}
                            </TableCell>
                            <TableCell>{movement.previous_quantity}</TableCell>
                            <TableCell>{movement.new_quantity}</TableCell>
                            <TableCell>{movement.reason || '-'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <InventoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                No Inventory Found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                No inventory item has been created for this menu item yet.
                Use the "Add Stock" button to create one.
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseViewStockDialog} color="inherit">
            Close
          </Button>
        
        </DialogActions>
      </Dialog>

      {/* ============================================ */}
      {/* SNACKBAR NOTIFICATIONS */}
      {/* ============================================ */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ============================================ */}
      {/* FLOATING ACTION BUTTON */}
      {/* ============================================ */}
      <Fab
        color="primary"
        aria-label="add menu item"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          display: { xs: 'flex', sm: 'none' },
          zIndex: 1000,
          backgroundColor: locationColor,
          '&:hover': {
            backgroundColor: locationColor,
            opacity: 0.8,
          },
        }}
        onClick={() => handleOpenDialog('create')}
      >
        <AddIcon />
      </Fab>
    </Suspense>
  );
};

export default MenuItemList;